import { Alert, Button, Card, Descriptions, Drawer, Empty, Input, Link, Modal, Spin, Table, Tabs, Timeline, Typography } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";

import { APPROVAL_ROUNDS_PAGE_SIZE } from "../config";
import { useApprovalInboxRuntime } from "../runtime/useApprovalInboxRuntime";
import {
  describeRoundStep,
  formatApprovalAmount,
  formatApprovalTime,
  formatRoundProgress,
  formatRoundStatus,
  formatStepApprovers,
  formatStepResult,
  resolveApprovalBusinessLink,
} from "../service/approvalRoundsService";
import type { ApprovalInboxTab, ApprovalRoundItem } from "../types";
import "./approval.css";

/**
 * 待我审批页：轮到我的和我处理过的两个列表，点开看业务摘要和每一级的处理记录，在这里通过或驳回。
 * 配了业务单地址的审批类型（应付请款）在详情里出「查看请款单」，在新标签页打开业务单，审批抽屉留着、看完回来接着处理。
 */
export function ApprovalInboxFeaturePage() {
  const runtime = useApprovalInboxRuntime();
  const detail = runtime.detail;
  const businessLink = detail ? resolveApprovalBusinessLink(detail.typeCode, detail.businessId) : null;
  const dialogText = runtime.dialog === "approve"
    ? { title: "通过这一级", ok: "确认通过", placeholder: "审批意见（选填）", okButtonProps: undefined }
    : { title: "驳回这一级", ok: "确认驳回", placeholder: "驳回意见（必填）", okButtonProps: { status: "danger" as const } };
  const columns: ColumnProps<ApprovalRoundItem>[] = [
    { title: "审批类型", dataIndex: "typeLabel", width: 110 },
    { title: "标题", dataIndex: "title" },
    { title: "金额", width: 140, render: (_, row) => formatApprovalAmount(row.amount) },
    { title: "申请人", dataIndex: "applicantName", width: 110 },
    { title: "提交时间", width: 180, render: (_, row) => formatApprovalTime(row.submittedAt) },
    { title: "进度", width: 150, render: (_, row) => formatRoundProgress(row.currentStepNo, row.stepCount) },
    ...(runtime.tab === "handled"
      ? [
          { title: "我的处理", width: 100, render: (_: unknown, row: ApprovalRoundItem) => (row.myResult ? formatStepResult(row.myResult) : "—") },
          { title: "状态", width: 100, render: (_: unknown, row: ApprovalRoundItem) => formatRoundStatus(row.status) },
        ]
      : []),
    {
      title: "操作",
      width: 90,
      render: (_, row) => (
        <Button type="text" aria-label={`查看${row.title}`} onClick={() => runtime.openRound(row.roundId)}>查看</Button>
      ),
    },
  ];
  return (
    <main className="approval-page">
      <Card>
        <Tabs activeTab={runtime.tab} onChange={(key) => runtime.setTab(key as ApprovalInboxTab)}>
          <Tabs.TabPane key="pending" title="待我审批" />
          <Tabs.TabPane key="handled" title="我已审批" />
        </Tabs>
        {runtime.listError && <Alert type="error" content={runtime.listError} action={<Button onClick={runtime.refreshList}>重试</Button>} />}
        <Table<ApprovalRoundItem>
          rowKey="roundId"
          loading={runtime.loading}
          data={runtime.items}
          columns={columns}
          noDataElement={<Empty description={runtime.tab === "pending" ? "现在没有轮到你审批的单子" : "你还没有处理过单子"} />}
          pagination={{ current: runtime.page, pageSize: APPROVAL_ROUNDS_PAGE_SIZE, total: runtime.total, onChange: runtime.setPage }}
        />
      </Card>
      <Drawer
        title={detail?.title ?? "审批单"}
        className="approval-inbox-drawer"
        visible={runtime.roundId !== null}
        width={640}
        onCancel={runtime.closeRound}
        footer={detail?.canHandle
          ? (
              <div className="approval-drawer-footer">
                <Button status="danger" onClick={() => runtime.openDialog("reject")}>驳回</Button>
                <Button type="primary" onClick={() => runtime.openDialog("approve")}>通过</Button>
              </div>
            )
          : null}
        unmountOnExit
      >
        {!detail && !runtime.detailError && runtime.detailLoading && <Spin block className="approval-detail-loading" tip="正在读取…" />}
        {runtime.detailError && <Alert type="error" content={runtime.detailError} />}
        {runtime.actionError && <Alert className="approval-detail-alert" type="warning" content={runtime.actionError} />}
        {detail && (
          <div className="approval-detail">
            <Descriptions
              title="业务摘要"
              column={1}
              data={[
                { label: "审批类型", value: detail.typeLabel },
                { label: "金额", value: formatApprovalAmount(detail.amount) },
                { label: "申请人", value: detail.applicantName },
                { label: "提交时间", value: formatApprovalTime(detail.submittedAt) },
                { label: "走的流程", value: detail.flowLabel },
                { label: "状态", value: `${formatRoundStatus(detail.status)}（第 ${detail.roundNo} 轮）` },
              ]}
            />
            {detail.summaryLines.length > 0 && (
              <ul className="approval-summary-lines">
                {detail.summaryLines.map((line) => <li key={line}>{line}</li>)}
              </ul>
            )}
            {businessLink && (
              <div>
                <Link href={businessLink.href} target="_blank" rel="noopener noreferrer">{businessLink.label}</Link>
              </div>
            )}
            {detail.rounds.map((round) => (
              <section key={round.roundId} className="approval-round">
                {detail.rounds.length > 1 && (
                  <Typography.Text bold>
                    {`第 ${round.roundNo} 轮 · ${round.flowLabel} · ${formatRoundStatus(round.status)}${round.roundId === detail.roundId ? "（当前查看）" : ""}`}
                  </Typography.Text>
                )}
                <Timeline>
                  {round.steps.map((step) => (
                    <Timeline.Item key={step.stepNo}>
                      <div data-round-step className="approval-round-step">
                        <span className="approval-round-step-title">{`第 ${step.stepNo} 级`}</span>
                        <span>{formatStepApprovers(step)}</span>
                        <Typography.Text type={step.result === "rejected" ? "error" : "secondary"}>{describeRoundStep(round, step)}</Typography.Text>
                        {step.remark && <span>{`意见：${step.remark}`}</span>}
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </section>
            ))}
          </div>
        )}
      </Drawer>
      <Modal
        title={dialogText.title}
        visible={runtime.dialog !== null}
        okText={dialogText.ok}
        okButtonProps={dialogText.okButtonProps}
        confirmLoading={runtime.acting}
        onOk={() => { void runtime.act(); }}
        onCancel={runtime.closeDialog}
        unmountOnExit
      >
        {detail && <p className="approval-dialog-target">{`第 ${detail.currentStepNo} 级 · ${detail.title}`}</p>}
        <Input.TextArea
          placeholder={dialogText.placeholder}
          maxLength={500}
          value={runtime.remark}
          onChange={runtime.setRemark}
        />
        {runtime.remarkError && <Typography.Text type="error">{runtime.remarkError}</Typography.Text>}
      </Modal>
    </main>
  );
}
